// Shared constants for the CRM module — types, statuses, sources, follow-up
// kinds/directions/outcomes, color maps, default form shape.
import { MessageCircle, Mail, Phone, CalendarDays, Sparkles } from "lucide-react";

export const TYPES = ["Fan", "Model", "Escort", "Studio", "Brand", "Team", "Producer", "Cameraman", "Event Organizer"];

export const TYPE_LABELS = {
  Fan: "Fan",
  Model: "Model",
  Escort: "Escort",
  Studio: "Studio / Agency",
  Brand: "Brand / Sponsor",
  Team: "Team Member",
  Producer: "Producer",
  Cameraman: "Cameraman",
  "Event Organizer": "Event Organizer"
};

export const typeLabel = (type) => TYPE_LABELS[type] || type || "Contact";

export const STATUSES = [
  "new_lead", "approved",
  // Per-shoot progression (all model/talent variants share this)
  "1st_shoot_completed", "2nd_shoot_completed", "3rd_shoot_completed",
  "4th_shoot_completed", "5th_shoot_completed", "6th_shoot_completed",
  "7th_shoot_completed", "8th_shoot_completed", "9th_shoot_completed",
  "10th_shoot_completed", "11th_shoot_completed", "12th_shoot_completed",
  "13th_shoot_completed", "14th_shoot_completed", "15th_shoot_completed",
  "16th_shoot_completed", "17th_shoot_completed", "18th_shoot_completed",
  "19th_shoot_completed", "20th_shoot_completed",
  // Event progression (Performer + Event Organizer); keys retained for compatibility.
  "1st_show_completed", "2nd_show_completed", "3rd_show_completed",
  "4th_show_completed", "5th_show_completed",
  "retired", "rip",
  "rejected", "archived",
];

export function statusesForType() {
  return STATUSES;
}

const LIFECYCLE_STATUSES = new Set(["new_lead", "approved", "rejected", "archived", "retired", "rip"]);
const PRIMARY_STATUS_PRIORITY = ["rip", "retired", "archived", "rejected", "approved", "new_lead"];
const STATUS_ORDER = new Map(STATUSES.map((status, index) => [status, index]));

function statusGroup(status) {
  if (LIFECYCLE_STATUSES.has(status)) return "lifecycle";
  if (/^\d+(st|nd|rd|th)_shoot_completed$/.test(status)) return "shoot";
  if (/^\d+(st|nd|rd|th)_show_completed$/.test(status)) return "event";
  return status;
}

export function getRecordStatuses(record) {
  if (Array.isArray(record?.statuses) && record.statuses.length) return record.statuses;
  return record?.status ? [record.status] : ["new_lead"];
}

export function matchesStatusFilter(record, statusFilters, mode = "any") {
  const filters = Array.isArray(statusFilters)
    ? statusFilters
    : statusFilters && statusFilters !== "all" ? [statusFilters] : [];
  if (filters.length === 0) return true;
  const recordStatuses = new Set(getRecordStatuses(record));
  return mode === "all"
    ? filters.every((status) => recordStatuses.has(status))
    : filters.some((status) => recordStatuses.has(status));
}

export function toggleRecordStatus(statuses, status) {
  const selected = Array.isArray(statuses) ? statuses : [];
  if (selected.includes(status)) return selected.filter((value) => value !== status);
  const group = statusGroup(status);
  return [...selected.filter((value) => statusGroup(value) !== group), status]
    .sort((a, b) => (STATUS_ORDER.get(a) ?? Number.MAX_SAFE_INTEGER) - (STATUS_ORDER.get(b) ?? Number.MAX_SAFE_INTEGER));
}

export function primaryStatus(statuses) {
  const selected = Array.isArray(statuses) ? statuses : [];
  const lifecycle = PRIMARY_STATUS_PRIORITY.find((status) => selected.includes(status));
  if (lifecycle) return lifecycle;
  const ordered = [...selected].sort((a, b) => (STATUS_ORDER.get(a) ?? -1) - (STATUS_ORDER.get(b) ?? -1));
  return ordered[ordered.length - 1] || "new_lead";
}

export const SOURCES = ["Form", "Organic", "Campaign", "Referral", "Other"];

export const FOLLOWUP_KINDS = [
  { v: "dm", label: "DM", icon: MessageCircle },
  { v: "email", label: "Email", icon: Mail },
  { v: "call", label: "Call", icon: Phone },
  { v: "meeting", label: "Meeting", icon: CalendarDays },
  { v: "note", label: "Note", icon: Sparkles },
  { v: "other", label: "Other", icon: Sparkles },
];

export const FU_DIRECTIONS = [
  { v: "outbound", label: "↑ Outbound (I reached out)" },
  { v: "inbound",  label: "↓ Inbound (they reached out)" },
];

export const FU_OUTCOMES = [
  { v: "neutral",  label: "Neutral",  hex: "#94a3b8" },
  { v: "positive", label: "Positive", hex: "#22c55e" },
  { v: "negative", label: "Negative", hex: "#ef4444" },
];

export const STATUS_COLORS = {
  new_lead: "text-[color:var(--gio-primary)] border-[color:var(--gio-primary)]",
  approved: "text-[color:var(--gio-secondary)] border-[color:var(--gio-secondary)]",
  "1st_shoot_completed": "text-green-300 border-green-700",
  "2nd_shoot_completed": "text-green-300 border-green-700",
  "3rd_shoot_completed": "text-green-300 border-green-700",
  "4th_shoot_completed": "text-green-300 border-green-700",
  "5th_shoot_completed": "text-emerald-300 border-emerald-700",
  "6th_shoot_completed": "text-emerald-300 border-emerald-700",
  "7th_shoot_completed": "text-emerald-300 border-emerald-700",
  "8th_shoot_completed": "text-emerald-200 border-emerald-700",
  "9th_shoot_completed": "text-emerald-200 border-emerald-700",
  "10th_shoot_completed": "text-amber-300 border-amber-600",
  "11th_shoot_completed": "text-amber-300 border-amber-600",
  "12th_shoot_completed": "text-amber-300 border-amber-600",
  "13th_shoot_completed": "text-amber-300 border-amber-600",
  "14th_shoot_completed": "text-amber-200 border-amber-500",
  "15th_shoot_completed": "text-amber-200 border-amber-500",
  "16th_shoot_completed": "text-amber-200 border-amber-500",
  "17th_shoot_completed": "text-amber-100 border-amber-400",
  "18th_shoot_completed": "text-amber-100 border-amber-400",
  "19th_shoot_completed": "text-yellow-100 border-yellow-400",
  "20th_shoot_completed": "text-yellow-100 border-yellow-300",
  "1st_show_completed": "text-pink-300 border-pink-700",
  "2nd_show_completed": "text-pink-300 border-pink-700",
  "3rd_show_completed": "text-pink-200 border-pink-600",
  "4th_show_completed": "text-pink-200 border-pink-500",
  "5th_show_completed": "text-pink-100 border-pink-400",
  rejected: "text-zinc-500 border-zinc-700",
  archived: "text-zinc-700 border-zinc-800",
};

const _ordinal = (n) => {
  const s = ["th","st","nd","rd"], v = n % 100;
  return n + (s[(v-20)%10] || s[v] || s[0]);
};
const _shootLabels = Object.fromEntries(
  Array.from({length: 20}, (_, i) => [`${_ordinal(i+1)}_shoot_completed`, `${_ordinal(i+1)} shoot ✓`])
);
const _showLabels = Object.fromEntries(
  Array.from({length: 5}, (_, i) => [`${_ordinal(i+1)}_show_completed`, `${_ordinal(i+1)} event ✓`])
);

export const STATUS_LABEL = {
  new_lead: "new lead",
  approved: "approved",
  ..._shootLabels,
  ..._showLabels,
  "10th_shoot_completed": "🏆 10th shoot",
  "20th_shoot_completed": "👑 20th shoot",
  retired: "🌅 retired",
  rip: "🕯️ RIP",
  rejected: "rejected",
  archived: "archived",
};

export const TYPE_COLORS = {
  "Model": "bg-pink-400/10 text-pink-400 border-pink-700",
  "Escort": "bg-purple-400/10 text-purple-400 border-purple-700",
  "Brand": "bg-yellow-400/10 text-yellow-400 border-yellow-700",
  "Fan": "bg-blue-400/10 text-blue-400 border-blue-700",
  "Studio": "bg-purple-400/10 text-purple-400 border-purple-700",
  "Team": "bg-green-400/10 text-green-400 border-green-700",
  "Producer": "bg-indigo-400/10 text-indigo-400 border-indigo-700",
  "Cameraman": "bg-teal-400/10 text-teal-400 border-teal-700",
  "Event Organizer": "bg-orange-400/10 text-orange-400 border-orange-700",
};

export const TYPE_HEX = {
  "Model": "#ec4899", 
  "Escort": "#a855f7", 
  "Brand": "#f59e0b", 
  "Fan": "#60a5fa",
  "Studio": "#a855f7", 
  "Team": "#22c55e",
  "Producer": "#6366f1",
  "Cameraman": "#14b8a6",
  "Event Organizer": "#fb923c",
};

export const STATUS_HEX = {
  new_lead: "#14B8A6", approved: "#06B6D4",
  "1st_shoot_completed": "#22c55e", "2nd_shoot_completed": "#22c55e", "3rd_shoot_completed": "#22c55e",
  "4th_shoot_completed": "#16a34a", "5th_shoot_completed": "#10b981", "6th_shoot_completed": "#10b981",
  "7th_shoot_completed": "#059669", "8th_shoot_completed": "#059669", "9th_shoot_completed": "#fbbf24",
  "10th_shoot_completed": "#f59e0b",
  "11th_shoot_completed": "#f59e0b", "12th_shoot_completed": "#f59e0b", "13th_shoot_completed": "#d97706",
  "14th_shoot_completed": "#d97706", "15th_shoot_completed": "#b45309",
  "16th_shoot_completed": "#b45309", "17th_shoot_completed": "#92400e",
  "18th_shoot_completed": "#92400e", "19th_shoot_completed": "#fbbf24",
  "20th_shoot_completed": "#facc15",
  "1st_show_completed": "#ec4899", "2nd_show_completed": "#db2777",
  "3rd_show_completed": "#be185d", "4th_show_completed": "#9d174d", "5th_show_completed": "#831843",
  retired: "#f59e0b", rip: "#3f3f46",
  rejected: "#71717a", archived: "#52525b",
};

export const EMPTY_FORM = {
  stage_name: "", legal_name: "", type: "Model", status: "new_lead", statuses: ["new_lead"], source: "Organic",
  email: "", phone: "", twitter: "", instagram: "", onlyfans: "", onlyfans_free: "", justforfans: "", 
  bluesky: "", telegram: "", faphouse: "", link_in_bio: "", tiktok: "",
  country: "", city: "", position: "", body_type: "", dick_size: "", date_of_birth: "",
  height: "", weight: "", ethnicity: "", tags: [],
  languages: "", kinks: "", boundaries: "", notes: "", photos: [], legal_consent_status: "",
  shoots: [],  // list of {n, date, location, notes} — shoot history
  shows: [],   // legacy key: event history rows {n, date, location, notes}
  follow_ups: [], linked_video_ids: [], linked_studio_ids: [],
  type_details: {},  // type-specific fields (brand/producer/team/...) — see typeFields.js
};
